import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Layout,
  Table,
  Input,
  Button,
  Popconfirm,
  Space,
  Tooltip,
  Breadcrumb,
  Spin,
} from "antd";
import { useTranslation } from "react-i18next";
import { DeleteOutlined, UndoOutlined } from "@ant-design/icons";
import "antd/dist/antd.min.css";
import "../../styles/Main.scss";
import ButtonField from "../../common/htmlComponents/ButtonField";
import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../utils/Notifications";
import { getRequest, postRequest } from "../../axios";
import { authUserData } from "../../utils/Helper";
import { ReactComponent as Archiveicon } from "../../image/Archiveicon.svg";
import SearchIcon from "../../image/SearchIcon.svg";
import { Row } from "antd";
import { ReactComponent as ArchiveRestore } from "../../image/ArchiveRestore.svg";
const { Content } = Layout;
const { Search } = Input;

const ArchivedSuperAdminVendorList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [vendor, setVendor] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, settotalCount] = useState(0);
  const [selectedRow, setSelectedRow] = useState([]);
  const [reload, setReload] = useState(false);
  const [state, setState] = useState({
    page: 1,
    ordering: "id",
    pagesize: 10,
    search: "",
    total: 0,
  });

  const companyPercentage = authUserData()?.company_profile_perc
    ? authUserData()?.company_profile_perc
    : null;

  useEffect(() => {
    const getVendorOnboardingArchiveList = async () => {
      setLoading(true);

      try {
        let apiResponse = await getRequest(
          `superuser-vendor/archived_list/`,
          state
        );
        console.log("api Res", apiResponse);
        if (apiResponse.status) {
          const sortedData = apiResponse.data.results.data.sort((a, b) => {
            return a.id - b.id;
          });
          setVendor(sortedData);
          settotalCount(apiResponse.data.count);
        } else {
          ErrorNotificationMsg(t("error_in_archivelist"));
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    getVendorOnboardingArchiveList();
  }, [state, t]);

  // Table columns
  const columns = [
    {
      title: "Vendor Name",
      dataIndex: "vendor_trade_name",
      key: "vendor_trade_name",
      sorter: true,
      width: 180,
    },
    {
      title: "Legal Name of Business",
      dataIndex: "vendor_registered_name",
      key: "vendor_registered_name",
      sorter: true,
      width: 200,
    },
    {
      title: "GSTN",
      dataIndex: "gst_no",
      key: "gst_no",
      sorter: true,
      width: 150,
    },
    {
      title: "Address",
      dataIndex: "trading_address_building",
      key: "trading_address_building",
      sorter: true,
      width: 200,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: true,
      width: 200,
    },
    {
      title: "Action",
      key: "operation",
      fixed: "right",
      width: 100,
      render: (text, record) => (
        <Space>
          <Popconfirm
            className="action"
            title={t("are_you_sure_to_restore")}
            okText={t("pop_ok")}
            cancelText={t("pop_cancel")}
            onConfirm={() => handleRestore(record.id)}
          >
            <Tooltip title="Restore Vendor">
              <ArchiveRestore className="ArchiverestorImg" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Table row selection
  const rowSelection = {
    onSelect: (record, selected, selectedRows) => {
      const updatedSelectedRowKeys = selected
        ? [...selectedRow, record.id]
        : selectedRow.filter((key) => key !== record.id);
      setSelectedRow(updatedSelectedRowKeys);
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      const updatedSelectedRowKeys = selected
        ? [...selectedRow, ...changeRows.map((row) => row.id)]
        : selectedRow.filter(
            (key) => !changeRows.find((row) => row.id === key)
          );
      setSelectedRow(updatedSelectedRowKeys);
    },
    selectedRowKeys: selectedRow,
  };

  // Handle table change (pagination, sorting)
  const handleTableChange = (pagination, filters, sorter) => {
    let orderBy = "id";
    if (sorter && sorter.columnKey && sorter.order) {
      orderBy =
        sorter.order === "ascend" ? sorter.columnKey : `-${sorter.columnKey}`;
    }
    setState({
      ...state,
      page: pagination.current,
      pagesize: pagination.pageSize,
      ordering: orderBy,
    });
    setSelectedRow(selectedRow);
  };

  // Debounced search
  let debounceTimeout = null;
  const onSearch = (value) => {
    if (debounceTimeout) clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(() => {
      if (value) {
        setState({
          ...state,
          search: value,
          page: 1,
        });
      } else {
        const { search, ...newState } = state;
        setState({ ...newState, page: 1 });
      }
    }, 500);
  };

  const handleRestore = async (id) => {
    try {
      let apiResponse = await postRequest("superuser-vendor/restore/", {
        deleted: [id],
      });
      if (apiResponse.data.success) {
        SuccessNotificationMsg(t("Vendor restored successfully"));
        setReload(!reload);
        navigate("/vendor-superadmin");
      } else {
        ErrorNotificationMsg(t("error_in_restore_vendor_onboarding"));
      }
    } catch (error) {
      ErrorNotificationMsg(error);
    }
  };

  // Bulk restore
  const handleBulkRestore = async () => {
    try {
      let apiResponse = await postRequest("superuser-vendor/restore/", {
        deleted: selectedRow,
      });
      if (apiResponse.data.success) {
        SuccessNotificationMsg(t("Vendors restored successfully"));
        setReload(!reload);
        setSelectedRow([]);
        navigate("/vendor-superadmin");
      } else {
        ErrorNotificationMsg(t("error_in_restore_vendor_onboarding"));
      }
    } catch (error) {
      ErrorNotificationMsg(error);
    }
  };

  const [companyProfileCount, setCompanyProfileCount] = useState(null);

  useEffect(() => {
    const fetchedData = authUserData()?.company_profile_count?.[0];
    if (fetchedData) {
      setCompanyProfileCount(fetchedData);
    } else {
      console.error("Company profile count data is missing or empty.");
    }
  }, [authUserData]);

  const incompleteModules = companyProfileCount
    ? Object.entries(companyProfileCount)
        .filter(([key, value]) => typeof value === "number" && value === 0)
        .map(([key]) => {
          switch (key) {
            case "company_material_perc":
              return "Company Material";
            case "site_location_perc":
              return "Site Location";
            case "user_role_perc":
              return "User Role";
            case "employee_perc":
              return "Employee";
            case "pr_release_perc":
              return "PR Release";
            case "business_setting_perc":
              return "Business Setting";
            case "vendor_perc":
              return "Vendor";
            default:
              return null;
          }
        })
        .filter((module) => module !== null)
    : [];

  const isComplete = incompleteModules.length === 0;

  return (
    <>
            <div className="ListingTopBar">
              <div className="pagenamewrap">
                <div className="pagename">
                  <Tooltip
                    placement="right"
                    title={<span>View and restore archived vendors.</span>}
                  >
                    <h3>Archived Vendors</h3>
                  </Tooltip>
                  <Breadcrumb className="Breadcrumb-Sec">
                    <Breadcrumb.Item>
                      <Link to="/dashboard">{t("home")}</Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                      <Link to="/vendor-superadmin">Vendor Onboarding</Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>Archived Vendors</Breadcrumb.Item>
                  </Breadcrumb>
                </div>
                <Space>
                  <div className="searchwrp">
                    <Space direction="vertical">
                      <Search
                        prefix={
                          <img
                            src={SearchIcon}
                            alt="SearchIcon"
                            className="SearchIcon"
                            width={22}
                          />
                        }
                        placeholder={t("pl_search_list")}
                        onChange={(e) => onSearch(e.target.value)}
                        style={{ width: 250 }}
                      />
                    </Space>
                  </div>
                  {selectedRow.length > 0 && (
                    <Popconfirm
                      className="action"
                      title={t("are_you_sure_to_restore")}
                      okText={t("pop_ok")}
                      cancelText={t("pop_cancel")}
                      placement="left"
                      onConfirm={() => handleBulkRestore()}
                    >
                      <ButtonField
                        className="BlueShadowBtn"
                        type="primary"
                        htmlType="button"
                        label="Restore Selected"
                      />
                    </Popconfirm>
                  )}
                </Space>
              </div>
            </div>

            <section className="grid-sec mt-5 tablegridsec">
              <div className="PR-body">
                <div className="tablewrp">
                <Table
                  scroll={{ x: 1200 }}
                  rowSelection={rowSelection}
                  columns={columns}
                  rowKey={(record) => record.id}
                  dataSource={vendor}
                  loading={loading}
                  pagination={{
                    current: state.page,
                    pageSize: state.pagesize,
                    total: totalCount,
                    showTotal: (total) => `Total ${total} items`,
                    showSizeChanger: true,
                    locale: { items_per_page: "Items / Page" },
                  }}
                  onChange={handleTableChange}
                />
                </div>
              </div>
              <div className="FormFooter Container">
                <ButtonField
                  className="SaveGradiantBtn"
                  type="primary"
                  htmlType="button"
                  label={t("pl_back_to_list")}
                  onClick={() => navigate(`/vendor-superadmin`)}
                />
              </div>
            </section>
   
    </>
  );
};

export default ArchivedSuperAdminVendorList;
